import React from 'react';
import { motion } from 'framer-motion';
import { SunIcon, MoonIcon, StarIcon, LockIcon, SparklesIcon, CalendarIcon, ClockIcon, MapPinIcon, ArrowRightIcon, HeartIcon, TargetIcon, BriefcaseIcon, TrendingUpIcon, ActivityIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Card } from '../ui/Card';
import { useCart } from '../../contexts/CartContext';
type ServiceType = 'natal' | 'solar' | 'compatibility' | 'combo' | 'forecast';
interface BetaReportPreviewProps {
  serviceType: ServiceType;
  onNavigate?: (page: string) => void;
}
// ═══════════════════════════════════════════
// SERVICE-SPECIFIC CONFIG
// ═══════════════════════════════════════════
const SERVICE_CONFIG: Record<ServiceType, {
  title: string;
  subtitle: string;
  gradient: string;
  accentColor: string;
  person2?: {
    name: string;
    date: string;
    time: string;
    place: string;
  };
  extraInfo?: string;
  summary: string;
  lockedSections: string[];
}> = {
  natal: {
    title: 'Натальная карта',
    subtitle: 'Персональный астрологический анализ',
    gradient: 'from-slate-900 to-slate-800',
    accentColor: 'indigo',
    summary: `Ваша натальная карта показывает яркую и многогранную личность. Солнце во Льве наделяет вас природной харизмой, уверенностью и стремлением к самовыражению. Вы прирождённый лидер, который любит быть в центре внимания и вдохновлять окружающих.

Луна в Скорпионе добавляет глубину вашему внутреннему миру. За внешней уверенностью скрывается интенсивная эмоциональная жизнь. Вы обладаете сильной интуицией и способностью видеть скрытые мотивы людей.

Асцендент в Стрельце формирует ваш внешний образ — оптимистичный, открытый и любознательный. Люди воспринимают вас как человека с широким кругозором и философским взглядом на жизнь.`,
    lockedSections: ['Планеты в знаках зодиака', 'Планеты в домах гороскопа', 'Аспекты между планетами', 'Карьера и финансовый потенциал', 'Любовь и отношения', 'Кармические задачи', 'Таланты и предназначение', 'Рекомендации и компенсаторика']
  },
  solar: {
    title: 'Соляр (Прогноз на год)',
    subtitle: 'Краткий обзор тенденций 2026 года',
    gradient: 'from-slate-900 to-amber-900/80',
    accentColor: 'amber',
    extraInfo: 'Солярный год: 2026',
    summary: `2026 год станет для вас периодом значительного карьерного роста и профессиональной трансформации. Юпитер в 10 доме соляра открывает двери для повышения, признания и расширения влияния в вашей сфере.

Первая половина года (особенно март-май) будет наиболее активной и результативной. Это время для смелых шагов и запуска новых проектов.

В сфере отношений год обещает быть более спокойным, фокус внимания будет смещен на социальную реализацию и достижение материальных целей.`,
    lockedSections: ['Подробный анализ темы года', 'Помесячный прогноз (12 месяцев)', 'Карьера и финансы в этом году', 'Отношения и личная жизнь', 'Здоровье и энергетика', 'Календарь ключевых дат', 'Анализ солярных аспектов', 'Практические рекомендации на год']
  },
  compatibility: {
    title: 'Совместимость (Синастрия)',
    subtitle: 'Базовый анализ отношений',
    gradient: 'from-slate-900 to-rose-900/80',
    accentColor: 'rose',
    person2: {
      name: 'Мария Иванова',
      date: '23 марта 1994',
      time: '09:15',
      place: 'Санкт-Петербург'
    },
    summary: `Ваша пара обладает высоким потенциалом для глубоких, романтичных отношений. Обе Луны находятся в водных знаках (Скорпион и Рак), что создаёт невероятно сильную эмоциональную связь и интуитивное понимание друг друга.

Солнце во Льве и Солнце в Рыбах могут иметь разные подходы к самовыражению, но это создает интересный баланс между яркостью одного партнера и глубиной другого.

Главный вызов для вашей пары — научиться балансировать потребность в слиянии с необходимостью личного пространства.`,
    lockedSections: ['Детальный анализ совместимости', 'Сексуальная совместимость', 'Коммуникация и решение конфликтов', 'Быт, финансы и совместные цели', 'Кармические связи между вами', 'Таблица всех аспектов', 'Долгосрочные перспективы', 'Рекомендации для гармонии']
  },
  combo: {
    title: 'Комбо: Натал + Соляр',
    subtitle: 'Базовый анализ личности и года',
    gradient: 'from-slate-900 to-purple-900/80',
    accentColor: 'purple',
    extraInfo: 'Солярный год: 2026',
    summary: `Ваша натальная карта показывает яркую, харизматичную личность (Солнце во Льве) с глубоким эмоциональным миром (Луна в Скорпионе). Вы обладаете огромным потенциалом для лидерства и влияния на других людей.

В 2026 году эти врожденные качества получат мощную поддержку от транзитного Юпитера. Год будет сфокусирован на карьерном росте и социальной реализации.

Это редкое сочетание личного потенциала и благоприятных внешних обстоятельств, которое бывает раз в 12 лет.`,
    lockedSections: ['Полный разбор натальной карты', 'Все планеты, дома и аспекты', 'Кармические задачи и таланты', 'Детальный прогноз на 2026 год', 'Помесячный календарь событий', 'Интегрированный анализ', 'Финансы и отношения (Натал + Соляр)', 'Персональные рекомендации']
  },
  forecast: {
    title: 'Прогноз (Транзиты)',
    subtitle: 'Краткий обзор текущих влияний',
    gradient: 'from-slate-900 to-teal-900/80',
    accentColor: 'teal',
    extraInfo: 'Период: Март 2026 — Сентябрь 2026',
    summary: `Ближайшие месяцы принесут значительные перемены в вашу жизнь. Главным драйвером изменений выступает транзитный Юпитер, проходящий через ваш 10 дом карьеры. Это открывает окно возможностей для профессионального роста.

Одновременно Сатурн в 7 доме требует серьезного и ответственного подхода к партнерским отношениям. Возможны проверки на прочность существующих связей.

Уран в 1 доме продолжает процесс вашей личной трансформации, подталкивая к обновлению имиджа и способов самовыражения.`,
    lockedSections: ['Подробный анализ всех транзитов', 'Влияние медленных планет', 'Влияние быстрых планет', 'Помесячный прогноз', 'Карьерные и финансовые тренды', 'Прогноз по отношениям', 'Календарь благоприятных дней', 'Зоны риска и предупреждения']
  }
};
export function BetaReportPreview({
  serviceType,
  onNavigate
}: BetaReportPreviewProps) {
  const {
    addItem
  } = useCart();
  const config = SERVICE_CONFIG[serviceType];
  // ═══════════════════════════════════════════
  // HIGHLIGHT SECTIONS (Service-specific)
  // ═══════════════════════════════════════════
  const renderNatalHighlights = () => <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-17529">
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="bg-amber-50 rounded-xl p-4 border border-amber-100" data-id="element-17530">
        <div className="flex items-center gap-2 mb-2" data-id="element-17531">
          <SunIcon className="w-5 h-5 text-amber-500" data-id="element-17532" />
          <span className="text-xs font-bold text-amber-600 uppercase tracking-wider" data-id="element-17533">
            Солнце
          </span>
        </div>
        <div className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-id="element-17534">
          <span className="text-amber-500" data-id="element-17535">♌</span> Лев
        </div>
        <div className="text-xs text-slate-500 mt-1" data-id="element-17536">Огонь • Ядро личности</div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} className="bg-indigo-50 rounded-xl p-4 border border-indigo-100" data-id="element-17537">
        <div className="flex items-center gap-2 mb-2" data-id="element-17538">
          <MoonIcon className="w-5 h-5 text-indigo-500" data-id="element-17539" />
          <span className="text-xs font-bold text-indigo-600 uppercase tracking-wider" data-id="element-17540">
            Луна
          </span>
        </div>
        <div className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-id="element-17541">
          <span className="text-indigo-500" data-id="element-17542">♏</span> Скорпион
        </div>
        <div className="text-xs text-slate-500 mt-1" data-id="element-17543">Вода • Внутренний мир</div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} className="bg-purple-50 rounded-xl p-4 border border-purple-100" data-id="element-17544">
        <div className="flex items-center gap-2 mb-2" data-id="element-17545">
          <StarIcon className="w-5 h-5 text-purple-500" data-id="element-17546" />
          <span className="text-xs font-bold text-purple-600 uppercase tracking-wider" data-id="element-17547">
            Асцендент
          </span>
        </div>
        <div className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-id="element-17548">
          <span className="text-purple-500" data-id="element-17549">♐</span> Стрелец
        </div>
        <div className="text-xs text-slate-500 mt-1" data-id="element-17550">
          Огонь • Социальная маска
        </div>
      </motion.div>
    </div>;
  const renderSolarHighlights = () => <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-17551">
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="bg-amber-50 rounded-xl p-4 border border-amber-100" data-id="element-17552">
        <div className="flex items-center gap-2 mb-2" data-id="element-17553">
          <TargetIcon className="w-5 h-5 text-amber-500" data-id="element-17554" />
          <span className="text-xs font-bold text-amber-600 uppercase tracking-wider" data-id="element-17555">
            Тема года
          </span>
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-17556">Трансформация</div>
        <div className="text-xs text-slate-500 mt-1" data-id="element-17557">Карьера и статус</div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} className="bg-orange-50 rounded-xl p-4 border border-orange-100" data-id="element-17558">
        <div className="flex items-center gap-2 mb-2" data-id="element-17559">
          <StarIcon className="w-5 h-5 text-orange-500" data-id="element-17560" />
          <span className="text-xs font-bold text-orange-600 uppercase tracking-wider" data-id="element-17561">
            Управитель
          </span>
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-17562">Юпитер</div>
        <div className="text-xs text-slate-500 mt-1" data-id="element-17563">
          Расширение возможностей
        </div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} className="bg-emerald-50 rounded-xl p-4 border border-emerald-100" data-id="element-17564">
        <div className="flex items-center gap-2 mb-2" data-id="element-17565">
          <TrendingUpIcon className="w-5 h-5 text-emerald-500" data-id="element-17566" />
          <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider" data-id="element-17567">
            Топ период
          </span>
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-17568">Март — Май</div>
        <div className="text-xs text-slate-500 mt-1" data-id="element-17569">Максимум энергии</div>
      </motion.div>
    </div>;
  const renderCompatibilityHighlights = () => <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-17570">
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="bg-rose-50 rounded-xl p-4 border border-rose-100 text-center" data-id="element-17571">
        <div className="text-xs font-bold text-rose-600 uppercase tracking-wider mb-2" data-id="element-17572">
          Эмоции
        </div>
        <div className="text-3xl font-bold text-rose-500 mb-1" data-id="element-17573">85%</div>
        <div className="text-xs text-slate-500" data-id="element-17574">Высокая совместимость</div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} className="bg-pink-50 rounded-xl p-4 border border-pink-100 text-center" data-id="element-17575">
        <div className="text-xs font-bold text-pink-600 uppercase tracking-wider mb-2" data-id="element-17576">
          Романтика
        </div>
        <div className="text-3xl font-bold text-pink-500 mb-1" data-id="element-17577">92%</div>
        <div className="text-xs text-slate-500" data-id="element-17578">Отличное притяжение</div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} className="bg-sky-50 rounded-xl p-4 border border-sky-100 text-center" data-id="element-17579">
        <div className="text-xs font-bold text-sky-600 uppercase tracking-wider mb-2" data-id="element-17580">
          Общение
        </div>
        <div className="text-3xl font-bold text-sky-500 mb-1" data-id="element-17581">78%</div>
        <div className="text-xs text-slate-500" data-id="element-17582">Хорошее понимание</div>
      </motion.div>
    </div>;
  const renderComboHighlights = () => <div className="grid grid-cols-2 gap-4" data-id="element-17583">
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="bg-indigo-50 rounded-xl p-4 border border-indigo-100" data-id="element-17584">
        <div className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-1" data-id="element-17585">
          Личность
        </div>
        <div className="text-lg font-bold text-slate-900" data-id="element-17586">
          ♌ Лев / ♏ Скорпион
        </div>
      </motion.div>
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} className="bg-amber-50 rounded-xl p-4 border border-amber-100" data-id="element-17587">
        <div className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-1" data-id="element-17588">
          Тема 2026
        </div>
        <div className="text-lg font-bold text-slate-900" data-id="element-17589">Карьерный рост</div>
      </motion.div>
    </div>;
  const renderForecastHighlights = () => <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-17590">
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="bg-amber-50 rounded-xl p-4 border border-amber-100" data-id="element-17591">
        <div className="flex items-center gap-2 mb-2" data-id="element-17592">
          <div className="w-6 h-6 rounded-full bg-amber-200 text-amber-700 flex items-center justify-center font-bold" data-id="element-17593">
            ♃
          </div>
          <span className="text-xs font-bold text-amber-600 uppercase tracking-wider" data-id="element-17594">
            Юпитер
          </span>
        </div>
        <div className="text-lg font-bold text-slate-900" data-id="element-17595">в 10 доме</div>
        <div className="text-xs text-slate-500 mt-1" data-id="element-17596">Карьера и статус</div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} className="bg-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-17597">
        <div className="flex items-center gap-2 mb-2" data-id="element-17598">
          <div className="w-6 h-6 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center font-bold" data-id="element-17599">
            ♄
          </div>
          <span className="text-xs font-bold text-slate-600 uppercase tracking-wider" data-id="element-17600">
            Сатурн
          </span>
        </div>
        <div className="text-lg font-bold text-slate-900" data-id="element-17601">в 7 доме</div>
        <div className="text-xs text-slate-500 mt-1" data-id="element-17602">Уроки в отношениях</div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} className="bg-cyan-50 rounded-xl p-4 border border-cyan-100" data-id="element-17603">
        <div className="flex items-center gap-2 mb-2" data-id="element-17604">
          <div className="w-6 h-6 rounded-full bg-cyan-200 text-cyan-700 flex items-center justify-center font-bold" data-id="element-17605">
            ♅
          </div>
          <span className="text-xs font-bold text-cyan-600 uppercase tracking-wider" data-id="element-17606">
            Уран
          </span>
        </div>
        <div className="text-lg font-bold text-slate-900" data-id="element-17607">в 1 доме</div>
        <div className="text-xs text-slate-500 mt-1" data-id="element-17608">Трансформация</div>
      </motion.div>
    </div>;
  // ═══════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════
  return <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden max-w-5xl mx-auto" data-id="element-17609">
      {/* Report Header */}
      <div className={`bg-gradient-to-r ${config.gradient} text-white p-6 sm:p-8`} data-id="element-17610">
        <div className="flex items-center justify-between mb-4" data-id="element-17611">
          <div className="flex items-center gap-2 flex-wrap" data-id="element-17612">
            <Badge variant="neutral" className="bg-white/20 text-white border-white/30" data-id="element-17613">
              Beta отчёт
            </Badge>
            <Badge variant="neutral" className="bg-white/15 text-white/80 border-white/20 text-xs" data-id="element-17614">
              Полный отчёт:{' '}
              {serviceType === 'combo' ? '62' : serviceType === 'solar' ? '38' : serviceType === 'compatibility' ? '32' : serviceType === 'forecast' ? '30' : '35'}{' '}
              стр.
            </Badge>
          </div>
          <span className="text-xs text-slate-300" data-id="element-17615">
            Сгенерировано: {new Date().toLocaleDateString('ru-RU')}
          </span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold mb-2" data-id="element-17616">{config.title}</h1>
        <p className="text-slate-200 text-sm" data-id="element-17617">{config.subtitle}</p>
      </div>

      {/* Birth Data Section */}
      <div className="p-6 sm:p-8 border-b border-slate-100" data-id="element-17618">
        <h2 className="text-lg font-bold text-slate-900 mb-4" data-id="element-17619">
          Вводные данные
        </h2>

        <div className="bg-slate-50 rounded-xl p-4 sm:p-6 mb-4" data-id="element-17620">
          <div className="text-lg font-bold text-slate-900 mb-3" data-id="element-17621">
            Алексей Петров
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-17622">
            <div className="flex items-center gap-2 text-sm" data-id="element-17623">
              <CalendarIcon className={`w-4 h-4 text-${config.accentColor}-500`} data-id="element-17624" />
              <span className="text-slate-700" data-id="element-17625">15 августа 1992</span>
            </div>
            <div className="flex items-center gap-2 text-sm" data-id="element-17626">
              <ClockIcon className={`w-4 h-4 text-${config.accentColor}-500`} data-id="element-17627" />
              <span className="text-slate-700" data-id="element-17628">14:30</span>
            </div>
            <div className="flex items-center gap-2 text-sm" data-id="element-17629">
              <MapPinIcon className={`w-4 h-4 text-${config.accentColor}-500`} data-id="element-17630" />
              <span className="text-slate-700" data-id="element-17631">Москва, Россия</span>
            </div>
          </div>
        </div>

        {config.person2 && <div className="bg-slate-50 rounded-xl p-4 sm:p-6" data-id="element-17632">
            <div className="text-lg font-bold text-slate-900 mb-3" data-id="element-17633">
              {config.person2.name}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-17634">
              <div className="flex items-center gap-2 text-sm" data-id="element-17635">
                <CalendarIcon className={`w-4 h-4 text-${config.accentColor}-500`} data-id="element-17636" />
                <span className="text-slate-700" data-id="element-17637">{config.person2.date}</span>
              </div>
              <div className="flex items-center gap-2 text-sm" data-id="element-17638">
                <ClockIcon className={`w-4 h-4 text-${config.accentColor}-500`} data-id="element-17639" />
                <span className="text-slate-700" data-id="element-17640">{config.person2.time}</span>
              </div>
              <div className="flex items-center gap-2 text-sm" data-id="element-17641">
                <MapPinIcon className={`w-4 h-4 text-${config.accentColor}-500`} data-id="element-17642" />
                <span className="text-slate-700" data-id="element-17643">{config.person2.place}</span>
              </div>
            </div>
          </div>}

        {config.extraInfo && <div className={`mt-4 inline-block px-3 py-1.5 bg-${config.accentColor}-50 text-${config.accentColor}-700 rounded-lg text-sm font-medium`} data-id="element-17644">
            {config.extraInfo}
          </div>}
      </div>

      {/* Highlights Section */}
      <div className="p-6 sm:p-8 border-b border-slate-100" data-id="element-17645">
        <h2 className="text-lg font-bold text-slate-900 mb-4" data-id="element-17646">
          {serviceType === 'natal' ? 'Ваша «Большая тройка»' : serviceType === 'compatibility' ? 'Ключевые показатели' : 'Главные акценты'}
        </h2>

        {serviceType === 'natal' && renderNatalHighlights()}
        {serviceType === 'solar' && renderSolarHighlights()}
        {serviceType === 'compatibility' && renderCompatibilityHighlights()}
        {serviceType === 'combo' && renderComboHighlights()}
        {serviceType === 'forecast' && renderForecastHighlights()}
      </div>

      {/* Summary Text */}
      <div className="p-6 sm:p-8 border-b border-slate-100" data-id="element-17647">
        <h2 className="text-lg font-bold text-slate-900 mb-4" data-id="element-17648">
          Краткое резюме
        </h2>
        <div className="prose prose-slate max-w-none" data-id="element-17649">
          {config.summary.split('\n\n').map((paragraph, i) => <p key={i} className="text-slate-600 leading-relaxed mb-4 last:mb-0" data-id="element-17650">
              {paragraph}
            </p>)}
        </div>
      </div>

      {/* Locked Premium Content */}
      <div className="p-6 sm:p-8 bg-slate-50 relative" data-id="element-17651">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-50/80 to-slate-50 pointer-events-none" data-id="element-17652" />

        <div className="relative" data-id="element-17653">
          <div className="flex items-center gap-3 mb-6" data-id="element-17654">
            <div className={`w-10 h-10 rounded-full bg-${config.accentColor}-100 text-${config.accentColor}-600 flex items-center justify-center`} data-id="element-17655">
              <LockIcon className="w-5 h-5" data-id="element-17656" />
            </div>
            <div data-id="element-17657">
              <h2 className="text-lg font-bold text-slate-900" data-id="element-17658">
                Полный Premium отчёт
              </h2>
              <p className="text-sm text-slate-500" data-id="element-17659">
                Разблокируйте все разделы анализа
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6" data-id="element-17660">
            {config.lockedSections.map((section, i) => <motion.div key={i} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.1 * i
          }} className="flex items-center gap-2 text-sm text-slate-400 bg-white/60 rounded-lg px-3 py-2 border border-slate-200/50" data-id="element-17661">
                <LockIcon className="w-3.5 h-3.5 flex-shrink-0" data-id="element-17662" />
                <span className="blur-[1px]" data-id="element-17663">{section}</span>
              </motion.div>)}
          </div>

          <Card padding="md" className={`bg-white border-${config.accentColor}-200 shadow-lg`} data-id="element-17664">
            <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-17665">
              <div className="flex-1 text-center sm:text-left" data-id="element-17666">
                <div className="flex items-center justify-center sm:justify-start gap-2 mb-1" data-id="element-17667">
                  <SparklesIcon className={`w-5 h-5 text-${config.accentColor}-500`} data-id="element-17668" />
                  <span className="font-bold text-slate-900" data-id="element-17669">
                    Разблокировать полный отчёт
                  </span>
                </div>
                <p className="text-sm text-slate-500" data-id="element-17670">
                  {serviceType === 'combo' ? '60+' : '30-40'} страниц глубокого
                  анализа
                </p>
              </div>
              <div className="flex items-center gap-4" data-id="element-17671">
                <span className="text-2xl font-bold text-slate-900" data-id="element-17672">
                  {serviceType === 'combo' ? '6 900 ₽' : serviceType === 'compatibility' ? '4 900 ₽' : '3 900 ₽'}
                </span>
                {onNavigate && <Button variant="primary" onClick={() => {
                const priceMap: Record<ServiceType, number> = {
                  natal: 3900,
                  solar: 3900,
                  compatibility: 4900,
                  combo: 6900,
                  forecast: 3900
                };
                addItem({
                  id: `premium-${serviceType}-${Date.now()}`,
                  name: `Premium отчёт: ${config.title}`,
                  description: config.subtitle,
                  price: priceMap[serviceType],
                  type: 'report',
                  format: 'PDF-отчёт',
                  badges: [{
                    label: 'Premium',
                    variant: 'pro'
                  }]
                });
                onNavigate('checkout');
              }} className={`bg-${config.accentColor}-600 hover:bg-${config.accentColor}-700`} data-id="element-17673">
                    Купить
                    <ArrowRightIcon className="ml-2 w-4 h-4" data-id="element-17674" />
                  </Button>}
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="p-6 bg-slate-50 border-t border-slate-200" data-id="element-17675">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-17676">
          <div className="text-center sm:text-left" data-id="element-17677">
            <p className="text-sm font-medium text-slate-700" data-id="element-17678">
              Astriqo • Beta отчёт
            </p>
            <p className="text-xs text-slate-500 mt-1" data-id="element-17679">
              Для полного анализа закажите Premium отчёт или консультацию
              астролога
            </p>
          </div>
          {onNavigate && <button onClick={() => onNavigate('service-consultation')} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 hover:bg-slate-100 text-slate-600 hover:text-slate-800 transition-colors text-sm font-medium" data-id="element-17680">
              <SparklesIcon className="w-4 h-4" data-id="element-17681" />
              Заказать консультацию
            </button>}
        </div>
      </div>
    </div>;
}